import { Check, Pencil, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { AssistantTurn, SpecCorrection, ToggleInference } from "@/lib/api";
import { cn } from "@/lib/utils";

import { LANGUAGE_NAMES, titleCase } from "./utils";

/** The controls a requestor can put right. `work_mode` is the composer's call. */
const CORRECTABLE = ["target_language", "audience", "length", "framing"] as const;
type Correctable = (typeof CORRECTABLE)[number];

const LABEL: Record<Correctable, string> = {
  target_language: "Market",
  audience: "Audience",
  length: "Length",
  framing: "Framing",
};

const CHOICES: Record<Correctable, string[]> = {
  target_language: Object.keys(LANGUAGE_NAMES),
  audience: ["architect", "prospect", "homeowner"],
  length: ["3", "5", "8", "10", "12"],
  framing: ["product_first", "story_first"],
};

function display(field: Correctable, value: string): string {
  if (field === "target_language") {
    return LANGUAGE_NAMES[value as keyof typeof LANGUAGE_NAMES] ?? value;
  }
  if (field === "length") {
    return `${value} slides`;
  }
  return titleCase(value);
}

interface ReadAsProps {
  turn: AssistantTurn;
  /** Ask again with these controls set by hand. */
  onCorrect: (corrections: SpecCorrection) => void;
  isBusy: boolean;
}

/**
 * How the request was read, in the open — and correctable without retyping it.
 *
 * The composer pre-sets every control from the words someone used. That is only
 * trustworthy if they can see it: a market that cannot tell "German" was read as
 * the target market from "German" being the language of the sentence has no way
 * to know which of the two it got. Controls that fell back to a default are
 * marked as assumed rather than dressed up as detections.
 *
 * Correcting one re-asks the same question with that control pinned, so nobody
 * has to reword a sentence until the parser agrees with them.
 */
export function ReadAs({ turn, onCorrect, isBusy }: ReadAsProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const shown = (turn.inferences ?? []).filter((inference): inference is ToggleInference =>
    (CORRECTABLE as readonly string[]).includes(inference.field),
  );
  if (shown.length === 0) {
    return null;
  }

  const valueOf = (inference: ToggleInference) => draft[inference.field] ?? inference.value;

  const apply = () => {
    const corrections: SpecCorrection = {};
    for (const inference of shown) {
      const field = inference.field as Correctable;
      const value = draft[field];
      if (value === undefined || value === inference.value) {
        continue;
      }
      if (field === "length") {
        corrections.length = Number(value);
      } else if (field === "target_language") {
        corrections.target_language = value as SpecCorrection["target_language"];
      } else if (field === "audience") {
        corrections.audience = value as SpecCorrection["audience"];
      } else {
        corrections.framing = value as SpecCorrection["framing"];
      }
    }
    setEditing(false);
    setDraft({});
    if (Object.keys(corrections).length > 0) {
      onCorrect(corrections);
    }
  };

  return (
    <div className="border-border/70 bg-surface/60 flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg border px-2.5 py-2">
      <span className="type-label text-muted mr-1">Read as</span>

      {shown.map((inference) => {
        const field = inference.field as Correctable;
        return editing ? (
          <label key={field} className="flex items-center gap-1 text-xs">
            <span className="text-muted">{LABEL[field]}</span>
            <select
              value={valueOf(inference)}
              onChange={(event) =>
                setDraft((current) => ({ ...current, [field]: event.target.value }))
              }
              className="border-border bg-card text-foreground rounded-md border px-1.5 py-0.5 text-xs"
              aria-label={LABEL[field]}
            >
              {CHOICES[field].map((choice) => (
                <option key={choice} value={choice}>
                  {display(field, choice)}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <Tooltip key={field}>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  "rounded-md border px-1.5 py-0.5 text-xs",
                  inference.inferred
                    ? "border-border bg-card text-foreground"
                    : "border-border/60 text-muted border-dashed",
                )}
              >
                <span className="text-muted">{LABEL[field]} </span>
                {display(field, inference.value)}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {inference.inferred ? `From "${inference.evidence}"` : inference.evidence}
            </TooltipContent>
          </Tooltip>
        );
      })}

      {editing ? (
        <span className="ml-auto flex items-center gap-1">
          <Button size="sm" onClick={apply} disabled={isBusy}>
            <Check className="mr-1 h-3 w-3" />
            {isBusy ? "Asking…" : "Ask again"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditing(false);
              setDraft({});
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        </span>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={() => setEditing(true)}
          disabled={isBusy}
        >
          <Pencil className="mr-1 h-3 w-3" />
          Change
        </Button>
      )}
    </div>
  );
}
